import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Channel,
  Modules,
  PropsWithPopup,
  useAppContext,
  useConfig,
  usePdfViewerBridge,
  useScript,
  useStyle,
} from 'scbk-services';
import { formatUtils } from 'scbk-shared-module/utils';
import { DialogWrapper, RoundButton } from 'scbk-ui-web';
import { EdocTranType } from './enums';
import { useClip } from './hooks/useClip';
import { EdocViewParameter } from './types';

interface Props extends PropsWithPopup {
  params: EdocViewParameter;
  isPreview?: boolean;
  onComplete?: () => void;
}

const EdocViewComponent: React.FC<Props> = ({
  isOpen,
  setOpen,
  params,
  isPreview = false,
  onComplete,
}) => {
  // clipReport JS load 여부
  const [isReady, setIsReady] = useState<boolean>(false);
  const [report, setReport] = useState<any>(null);
  const [zoomIndex, setZoomIndex] = useState<number>(0);

  const { getContext: appContext } = useAppContext();
  const { doNPDFDownload } = usePdfViewerBridge();
  const { clipUrl, clipJsPath, clipCssPath, clipUserConfigCssPath } = useConfig().getConfig(
    Modules.COMMON,
  );

  // 다국어 처리
  const { t } = useTranslation();

  // clip report script
  useScript({
    src: `${clipUrl}${clipJsPath}`,
    strategy: 'afterInteractive',
    onReady: () => {
      setIsReady(true);
    },
    onError: () => {
      setIsReady(false);
    },
  });

  // clip report css
  useStyle({ href: `${clipUrl}${clipCssPath}` });
  useStyle({ href: `${clipUrl}${clipUserConfigCssPath}` });

  // clip report hook
  const {
    transfer,
    view,
    reportKey,
    isReportReady,
    currentPageCount,
    setCurrentPageCount,
    totalPageCount,
  } = useClip(clipUrl);

  console.log(currentPageCount, totalPageCount);

  const init = useCallback(async () => {
    const reportKey = await transfer(params);
    console.log('reportKey > ', reportKey);
    // setReportKey((data as string).trim().substring(23, 55));

    const { report } = await view('pdfViewerClip', reportKey as string);
    setReport(report);
  }, []);

  const pdfDownload = useCallback(async () => {
    try {
      const pdfUrl =
        params.tranType === EdocTranType.CERTIFICATES && !isPreview
          ? 'exportForPartPDFMarkAny.jsp'
          : 'exportForPartPDF2.jsp';

      const exportUrl = `${clipUrl}/clip/${pdfUrl}`;

      const formData = new URLSearchParams();
      formData.append('report_key', reportKey);
      formData.append('uid', reportKey);
      formData.append('crfFileName', params.crfFileName ?? '');
      formData.append('crfJsonData', params.crfJsonData ?? '');

      //PDF Blob 요청
      const response = await fetch(exportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        throw new Error('PDF 생성 실패');
      }

      const blob = await response.blob();

      //IE 대응
      if ((window.navigator as any).msSaveBlob) {
        (window.navigator as any).msSaveBlob(blob, params.pdfFileName);
        return;
      }

      //Blob URL 생성
      const urlLink = window.URL.createObjectURL(blob);

      //APP 처리
      if (appContext().channel === Channel.MB) {
        const reader = new FileReader();

        reader.onloadend = async () => {
          //   const base64Data = reader.result?.toString().replace('data:application/pdf,base64,', '');
          const base64Data = reader.result?.toString();
          await doNPDFDownload(base64Data ?? '', params.pdfFileName ?? 'report.pdf');
        };
        reader.readAsDataURL(blob);
      } else {
        const link = document.createElement('a');

        link.href = urlLink;
        link.download = params.pdfFileName ?? 'report.pdf';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(urlLink);
      }
    } catch (error) {
      console.error(error);
    }
  }, [reportKey]);

  const handleComplete = useCallback(async () => {
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    console.log(`zoomIndex effect : ${isReportReady}`);
    if (isReportReady) {
      console.log(`zoomIndex : ${zoomIndex}`);
      report.callChangeRatioCombo(zoomIndex == 0 ? 'PageWidth' : `${zoomIndex}`);
    }
  }, [zoomIndex]);

  useEffect(() => {
    if (isReportReady) {
      report.selectPage(`${currentPageCount}`);
    }
  }, [currentPageCount]);

  useEffect(() => {
    if (isReady) {
      init();
    }
  }, [isReady]);

  return (
    <>
      <DialogWrapper
        title={params.title ?? ''}
        open={isOpen}
        setOpen={setOpen}
        isFull={true}
        footer={[
          {
            type: 'primary',
            title: t('EdocViewComponent.001', '확인'),
            hasClose: true,
            handler: () => {
              console.log('EdocViewComponent > DialogWrapper - onClose');
              handleComplete();
            },
          },
        ]}
      >
        <div className="viewer-typeA viewer-wrapper">
          <div className="viewer-pagination">
            <RoundButton
              label={t('EdocViewComponent.002', '이전페이지')}
              icon="ico-caret-left"
              className="btn-prev"
              onClick={() => {
                if (currentPageCount > 1) {
                  setCurrentPageCount((prev) => prev - 1);
                }
              }}
            />
            <div className="viewer-current">
              {formatUtils.addZero(currentPageCount, 2)}/{formatUtils.addZero(totalPageCount, 2)}
            </div>
            <RoundButton
              label={t('EdocViewComponent.003', '다음페이지')}
              icon="ico-caret-right"
              className="btn-next"
              onClick={() => {
                if (currentPageCount < totalPageCount) {
                  setCurrentPageCount((prev) => prev + 1);
                }
              }}
            />
          </div>

          <div className="viewer-content-wrapper">
            <div id="pdfViewerClip" className="viewer-content" tabIndex={1}>
              {/* <div id="pdfViewerClip" className="viewer-content" ref={pdfViewer} tabIndex={1}> */}
              {/*PDF 영역*/}
            </div>
            {/* <select hidden ref={selectRef} defaultValue={zoomOptions[0]}>
              {zoomOptions.map((value, idx) => (
                <option key={idx} value={value}>
                  {(idx + 10) * 10}
                </option>
              ))}
            </select> */}
            <RoundButton
              label={t('EdocViewComponent.004', '확대')}
              icon="ico-plus"
              className="btn-zoom-in"
              onClick={() => {
                setZoomIndex((prev) => {
                  return prev === 0 ? 95 : prev === 195 ? prev : prev + 10;
                });
              }}
            />
            <RoundButton
              label={t('EdocViewComponent.005', '축소')}
              icon="ico-minus"
              className="btn-zoom-out"
              onClick={() => {
                setZoomIndex((prev) => {
                  return prev === 0 ? prev : prev === 95 ? 0 : prev - 10;
                });
              }}
            />
            {!isPreview && (
              <RoundButton
                label={t('EdocViewComponent.006', '다운로드')}
                icon="ico-download"
                className="btn-download"
                color="primary"
                onClick={() => {
                  pdfDownload();
                  // report?.pdfDownLoad();
                }}
              />
            )}
          </div>
        </div>
      </DialogWrapper>
    </>
  );
};

export default EdocViewComponent;
